import { IntlShape } from 'react-intl';
import rocketSvg from '../assets/icons/sports/rocket.svg';
import { KwiffTicketDetails, PendingBet, PendingTicket, TicketSearchResult } from '../data-types/betting/bettingInterfaces';
import { DEFAULT_MIN_BET_AMOUNT, eventStatuses, sportCategoryDetails } from '../data-types/sports/sportsConstants';
import { Outcome, Ticket, TicketSystemBetDetails, TicketSystemBetDetailsInfo } from '../data-types/sports/sportsInterfaces';
import { TicketFinalStatus } from '../data-types/ticket/ticketEnums';
import { SystemBetPlacedBetTitleData } from '../data-types/ticket/ticketInterfaces';
import { store } from '../state/store';
import { getPlaceOddsForEW } from './bet';
import { getOfferState, getOutcome } from './betslip';
import { isEventInState } from './event';

const isKwiffed = (ticket: Ticket): boolean => {
  const singleBetKwiffed = !ticket.accumulator && ticket.bets[0].animationEffect;
  const accumulatorKwiffed = ticket.accumulator && ticket.accumulator.animationEffect;
  const systemBetKwiffed = ticket.systemBetDetails ? isSystemBetBoosted(ticket.systemBetDetails) : false;

  const eachTypeOfTicketIsNotBoosted = !singleBetKwiffed && !accumulatorKwiffed && !systemBetKwiffed;
  if (eachTypeOfTicketIsNotBoosted) {
    return false;
  }

  const notSystemBetWithoutBalancedOdds =
    !systemBetKwiffed &&
    (!ticket.balancedWinOdds || !ticket.balancedOdds || !ticket.balancedFractional || !ticket.balancedWinOddsFractional);
  if (notSystemBetWithoutBalancedOdds) {
    return false;
  }

  return true;
};

/**
 * Checks if the kwiffed animation should be played for a given ticket.
 * Rules:
 * 1. The information contained in the ticket must not be corrupt
 * 2. The ticket must be kwiffed
 * 3. The ticket must be placed on the current device
 * @param ticket The ticket to verify
 * @param pendingTickets The pending tickets on the device
 * @returns boolean True, if the ticket is kwiffed and was placed on the current device.
 */
export const shouldDisplayKwiffedDialog = (ticket: Ticket, pendingTickets: PendingTicket): boolean =>
  isKwiffed(ticket) && !!pendingTickets[ticket.id];

export const isCashedOut = (ticket: Ticket): boolean => !!ticket.result && ticket.result.finalStatus === TicketFinalStatus.CASHOUT;

const getEarliestBetDateFromTicket = (ticket: Ticket): string | null => {
  const sortedBetsArray = ticket.bets.map(bet => bet.event.startDate).sort(stringifiedDateSorter);

  if (sortedBetsArray.length > 0) {
    return sortedBetsArray[0];
  }

  return null;
};

export const stringifiedDateSorter = (date1: string, date2: string) => new Date(date1).getTime() - new Date(date2).getTime();

export const sortByDate = (tickets: Ticket[]): Ticket[] =>
  [...tickets].sort((ticket1, ticket2) => {
    const ticket1Date = getEarliestBetDateFromTicket(ticket1);
    const ticket2Date = getEarliestBetDateFromTicket(ticket2);

    if (ticket1Date && ticket2Date) {
      return stringifiedDateSorter(ticket1Date, ticket2Date);
    }

    if (ticket1Date) {
      return -1;
    }

    if (ticket2Date) {
      return 1;
    }

    return 0;
  });

export const sortByTimePlaced = (tickets: Ticket[]): Ticket[] =>
  [...tickets].sort((ticket1, ticket2) => stringifiedDateSorter(ticket2.createdAt, ticket1.createdAt));

export const isAnyBetLive = (ticket: Ticket): boolean => ticket.bets.some(bet => isEventInState(bet.event, eventStatuses.LIVE));

export const mapToKwiffTicketDetails = (ticket: Ticket): KwiffTicketDetails => ({
  id: ticket.id,
  odds: ticket.type === 'EW' ? ticket.winOdds! : ticket.odds!,
  fractional: ticket.type === 'EW' ? ticket.winOddsFractional! : ticket.fractional!,
  balancedOdds: ticket.type === 'EW' ? ticket.balancedWinOdds! : ticket.balancedOdds!,
  balancedFractional: ticket.type === 'EW' ? ticket.balancedWinOddsFractional! : ticket.balancedFractional!,
  oddsForCalc: ticket.odds,
  balancedOddsForCalc: ticket.balancedOdds!,
  stake: ticket.amount,
  isEachWay: ticket.type === 'EW',
  multiplier: ticket.multiplier || 0,
  netPayout: ticket.netPayout,
  preBoostNetPayout: ticket.preBoosted.netPayout,
});

export const getEWOddsForEachBetForAccumulatorBet = (bets: PendingBet[], isEachWay: boolean, original?: boolean) =>
  bets.map(bet => {
    const offer = getOfferState(bet);

    if (!isEachWay) {
      return [original ? parseFloat(bet.originalOdds) : offer.odds, 1];
    }

    const outcome: Outcome | null = getOutcome(bet);
    if (outcome === null) {
      return [original ? parseFloat(bet.originalOdds) : offer.odds, 1];
    }

    return [
      original ? parseFloat(bet.originalOdds) : outcome.odds,
      getPlaceOddsForEW(
        outcome,
        bet.event.sportId,
        original ? bet.originalPlaceOdds : undefined,
        original ? bet.originalEachWayOdds : undefined,
      ),
    ];
  });

export const isBettingStakeBigEnough = (stake: number) => {
  const {
    userData: { user },
  } = store.getState();

  return stake >= ((user && user.currency && user.currency.minStake) || DEFAULT_MIN_BET_AMOUNT);
};

export const getTicketById = (ticketId: number, pendingTicket: PendingTicket): TicketSearchResult | null => {
  if (pendingTicket[ticketId]) {
    return { ...pendingTicket[ticketId], id: ticketId };
  }

  return null;
};

export const getSystemBetTitleData = (ticket: Ticket, intl: IntlShape): SystemBetPlacedBetTitleData => {
  const { bets, selectionDetails } = ticket;
  const { formatMessage } = intl;

  const uniqueSportIds = Array.from(
    new Set(
      selectionDetails
        ? selectionDetails.filter(details => details.event && details.event.sportId).map(details => details.event.sportId)
        : bets.map(bet => bet.sportId),
    ),
  );

  const icon = uniqueSportIds.length === 1 ? sportCategoryDetails[uniqueSportIds[0]].svg || '' : rocketSvg;
  const titleComponents = uniqueSportIds.map(sportId => formatMessage({ id: sportCategoryDetails[sportId].nameId || '' }).toUpperCase());
  const title = titleComponents.join(' & ');

  return { icon, title };
};

export const getBiggestKwiffedTicketFromSystemBets = (
  systemBetDetails: TicketSystemBetDetails[],
): TicketSystemBetDetailsInfo | undefined => {
  let biggestKwiff: TicketSystemBetDetailsInfo | undefined;

  systemBetDetails.forEach(systemBetDetail =>
    systemBetDetail.tickets.forEach(ticket => {
      const hasMultiplier = ticket.multiplier !== null;
      if (!biggestKwiff && hasMultiplier) {
        biggestKwiff = ticket;
      } else if (biggestKwiff && biggestKwiff.multiplier && ticket.multiplier && biggestKwiff.multiplier < ticket.multiplier) {
        biggestKwiff = ticket;
      }
    }),
  );

  return biggestKwiff;
};

export const isSystemBetBoosted = (systemBetDetails: TicketSystemBetDetails[]): boolean => {
  let hasBoostedTickets = false;

  for (const systemBetDetail of systemBetDetails) {
    if (hasBoostedTickets) {
      break;
    }

    for (const ticket of systemBetDetail.tickets) {
      if (hasBoostedTickets) {
        break;
      }

      hasBoostedTickets = ticket.multiplier !== null;
    }
  }

  return hasBoostedTickets;
};
